import React from 'react';
import { cn } from '../../lib/utils';
import { ChevronDown } from 'lucide-react';
import { usePageContext, generateAutoTrackingId, pushGTMEvent } from '../../utils/tracking';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  className?: string;
  // Tracking props
  trackingId?: string; // Custom tracking ID override
  trackingSection?: string; // Section context for tracking
  disableTracking?: boolean; // Disable automatic tracking
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ 
    className, 
    value, 
    onValueChange, 
    placeholder, 
    options, 
    trackingId,
    trackingSection,
    disableTracking = false,
    ...props 
  }, ref) => {
    const { pageName, section } = usePageContext();
    
    // Generate tracking ID if not provided and tracking is enabled
    const autoTrackingId = !disableTracking && !trackingId ? 
      generateAutoTrackingId({
        elementType: 'select',
        pageName,
        section: trackingSection || section,
        placeholder,
        title: props.title
      }) : trackingId;

    // Enhanced onChange handler with tracking
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = e.target.value;
      const selectedOption = options.find(opt => opt.value === selectedValue);
      
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'select',
          elementId: autoTrackingId,
          elementText: selectedOption?.label || selectedValue,
          pageName,
          section: trackingSection || section,
          variant: 'dropdown',
          userAction: 'change'
        });
      }
      
      if (onValueChange) {
        onValueChange(selectedValue);
      }
    };

    const handleFocus = () => {
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'select',
          elementId: autoTrackingId,
          elementText: placeholder,
          pageName,
          section: trackingSection || section,
          variant: 'dropdown',
          userAction: 'focus'
        });
      }
    };

    return (
      <div className="relative">
        <select
          className={cn(
            'flex h-9 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1 text-sm text-gray-900 dark:text-gray-100 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50 appearance-none',
            className
          )}
          ref={ref}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          id={autoTrackingId}
          data-tracking-id={autoTrackingId}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
      </div>
    );
  }
);
Select.displayName = 'Select';

export { Select };